import React, {useState} from "react"
import {AsyncResultIdleRequestState} from "../../types/asyncResultIdle"
import {AsyncResultModal} from "../AsyncResultModal/AsyncResultModal"
import {ErrorDocument, RiskRate} from "../../resources/common"
import {DefaultRiskRate} from "../../resources/org"
import {refreshAndClose} from "../../utilities/modals"

interface ApproveApplicationModalBaseProps<T> {
	title: string
	close: () => void
	onSuccess: () => void
	state: AsyncResultIdleRequestState<T, ErrorDocument>
}

export type ApproveApplicationModalProps<T> =
	| (ApproveApplicationModalBaseProps<T> & {
			showRiskRate: true
			defaultRiskRate: DefaultRiskRate
			approveFn: (reason: string, riskRate: RiskRate) => void
	  })
	| (ApproveApplicationModalBaseProps<T> & {
			showRiskRate: false
			approveFn: (reason: string) => void
	  })

export function ApproveApplicationModal<T>(props: ApproveApplicationModalProps<T>) {
	const {title, close, onSuccess, approveFn, state, showRiskRate} = props
	const [reason, setReason] = useState("")
	const [riskRate, setRiskRate] = useState<RiskRate>(
		showRiskRate ? props.defaultRiskRate.attributes.defaultCustomerRiskRate : RiskRate.Low
	)

	return (
		<AsyncResultModal
			title={title}
			close={refreshAndClose(onSuccess, close)}
			buttonClassname="is-success"
			state={state}
			buttonText="Approve"
			successText="Approved"
			onSubmit={() => (showRiskRate ? approveFn(reason, riskRate) : approveFn(reason))}
			errorToText={(err: ErrorDocument) => err.errors[0].title}
		>
			<div className="field">
				<label className="label">Common reasons</label>

				<div className="select is-fullwidth">
					<select
						defaultValue=""
						onChange={(e) => {
							setReason(e.target.value)
						}}
					>
						<option value="ID approved. ID, phone and IP address from same state.">
							ID approved. ID, phone and IP address from same state.
						</option>
						<option value="SSN miskeyed. Updated and reran and auto approved.">
							SSN miskeyed. Updated and reran and auto approved.
						</option>
						<option value="DOB miskeyed. Updated and reran and auto approved.">
							DOB miskeyed. Updated and reran and auto approved.
						</option>
						<option value="Alloy job failed due to invalid email address. Updated and reran and auto approved.">
							Alloy job failed due to invalid email address. Updated and reran and auto approved.
						</option>
						<option value="ID approved. Phone in a different state than ID and IP address but LexisNexis found address history in that state.">
							ID approved. Phone in a different state than ID and IP address but LexisNexis found address history in
							that state.
						</option>
						<option value="Passport verified name, DOB and ID number. Address verification letter verified address.">
							Passport verified name, DOB and ID number. Address verification letter verified address.
						</option>
						<option value="">Other</option>
					</select>
				</div>
			</div>
			<div className="field">
				<label className="label">Reason</label>
				<div className="control">
					<textarea
						required
						className="textarea"
						placeholder="Why are you approving this application?"
						value={reason}
						onChange={(e) => setReason(e.target.value)}
					/>
				</div>
			</div>
			{showRiskRate && (
				<div className="field">
					<label className="label">Customer Risk Rate</label>
					<div className="field-body">
						<div className="select">
							<select
								value={riskRate}
								onChange={(e) => {
									const val = e.target.value

									if (val == RiskRate.Low || val == RiskRate.Medium || val == RiskRate.High) {
										setRiskRate(val)
									}
								}}
							>
								<option value={RiskRate.Low} key={RiskRate.Low}>
									Low
								</option>
								<option value={RiskRate.Medium} key={RiskRate.Medium}>
									Medium
								</option>
								<option value={RiskRate.High} key={RiskRate.High}>
									High
								</option>
							</select>
						</div>
					</div>
				</div>
			)}
		</AsyncResultModal>
	)
}
